import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
    Building2,
    Star as StarIcon,
    MapPin,
    Hash,
    Hammer,
    Bed,
    Utensils,
    Sparkles as SpaIcon,
    Users,
    Waves,
    ArrowRight,
    ArrowLeft,
    Download,
    Mail,
    Calculator as CalcIcon,
    PhoneCall,
    Loader2,
    RefreshCw,
    Share2,
} from "lucide-react";
import {
    PieChart,
    Pie,
    Cell,
    Tooltip,
    ResponsiveContainer,
    Legend,
} from "recharts";
import CountUp from "react-countup";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import useSeo from "../../hooks/useSeo";
import StepIndicator from "../../components/calc/StepIndicator";
import { Switch } from "../../components/ui/switch";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "../../components/ui/dialog";
import ShareDialog from "../../components/calc/ShareDialog";

const STEPS = ["Tesis", "Detaylar", "Sonuç", "Aksiyonlar"];

const PIE_COLORS = ["#39c8c1", "#d6b06b", "#123b52", "#22c55e", "#0c2438", "#94a3b8"];
const BREAKDOWN_LABELS = {
    construction: "İnşaat",
    interior: "İç Mimari",
    equipment: "Ekipman",
    tech: "Teknoloji",
    legal: "Hukuk / İzin",
    working_capital: "İşletme Sermayesi",
};

const initialForm = {
    facility_type: "city",
    star_target: 4,
    city_code: "34",
    room_count: 100,
    building_status: "new",
    room_distribution: { standard: 60, deluxe: 30, suite: 10 },
    extras: {
        restaurant: { exists: true, capacity: 100 },
        spa: true,
        meeting_room: { exists: true, capacity: 80 },
        pool: true,
    },
    concept: "midscale",
};

export default function CostCalculator() {
    const { t } = useTranslation();
    const [step, setStep] = useState(0);
    const [defaults, setDefaults] = useState(null);
    const [form, setForm] = useState(initialForm);
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [emailModal, setEmailModal] = useState(false);
    const [shareModal, setShareModal] = useState(false);

    useSeo({
        title: "Maliyet Hesaplayıcı",
        description: "Otel maliyet hesaplayıcı: yıldız, oda sayısı, lokasyon ve konsepte göre detaylı yatırım analizi.",
        path: "/araclar/maliyet-hesaplayici",
    });

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/calculator/defaults");
                setDefaults(data);
            } catch (_) {}
        })();
    }, []);

    const update = (path, value) => {
        setForm((prev) => {
            const next = JSON.parse(JSON.stringify(prev));
            const keys = path.split(".");
            let obj = next;
            for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
            obj[keys[keys.length - 1]] = value;
            return next;
        });
    };

    const totalDistributed = useMemo(
        () =>
            (form.room_distribution.standard || 0) +
            (form.room_distribution.deluxe || 0) +
            (form.room_distribution.suite || 0),
        [form.room_distribution],
    );

    const calculate = async () => {
        setLoading(true);
        try {
            const { data } = await api.post("/calculator/cost", form);
            setResult(data);
            setStep(2);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    const downloadPdf = async () => {
        try {
            const res = await api.post("/calculator/cost/pdf", form, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = "otelpro-maliyet-raporu.pdf";
            a.click();
            window.URL.revokeObjectURL(url);
            toast.success("PDF indirildi");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const reset = () => {
        setForm(initialForm);
        setResult(null);
        setStep(0);
    };

    const cityName = (defaults?.cities || []).find((c) => c.code === form.city_code)?.name || form.city_code;

    return (
        <div className="bg-light-bg min-h-[80vh]" data-testid="cost-calculator-page">
            {/* Header */}
            <section className="bg-dark-navy text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#08111d] via-[#0c2438] to-[#123b52]" />
                <div className="relative max-w-5xl mx-auto px-6 py-12 lg:py-16">
                    <Link to="/araclar" className="text-xs uppercase tracking-[0.2em] text-teal font-semibold inline-flex items-center gap-1 hover:text-white">
                        <ArrowLeft className="w-3.5 h-3.5" /> Tüm araçlar
                    </Link>
                    <h1 className="mt-3 text-3xl sm:text-5xl font-bold tracking-tight inline-flex items-center gap-3">
                        <CalcIcon className="w-8 h-8 text-gold" />
                        Maliyet Hesaplayıcı
                    </h1>
                    <p className="mt-3 text-white/75 max-w-2xl">
                        Hedef yıldız, lokasyon ve konsepte göre otel yatırımının toplam maliyetini ve kategori bazında dağılımını detaylı hesaplayın.
                    </p>
                </div>
            </section>

            {/* Step indicator */}
            <div className="bg-white border-b border-slate-200">
                <div className="max-w-5xl mx-auto px-6 py-6">
                    <StepIndicator steps={STEPS} current={step} />
                </div>
            </div>

            {/* Body */}
            <div className="max-w-5xl mx-auto px-6 py-10">
                {step === 0 && (
                    <Card>
                        <h2 className="text-lg font-bold text-dark-navy">Adım 1 — Tesis Temel Bilgileri</h2>
                        <p className="text-sm text-deep-blue/70 mt-1">Tesis türü, hedef yıldız, lokasyon ve oda sayısı.</p>
                        <div className="mt-6 grid md:grid-cols-2 gap-5">
                            <Field label="Tesis Türü" Icon={Building2}>
                                <div className="grid grid-cols-2 gap-2">
                                    {(defaults?.facility_types || []).map((f) => (
                                        <SelectChip
                                            key={f.value}
                                            active={form.facility_type === f.value}
                                            onClick={() => update("facility_type", f.value)}
                                            data-testid={`cost-facility-${f.value}`}
                                        >
                                            {f.label}
                                        </SelectChip>
                                    ))}
                                </div>
                            </Field>
                            <Field label="Hedef Yıldız" Icon={StarIcon}>
                                <div className="flex gap-2">
                                    {[1, 2, 3, 4, 5].map((s) => (
                                        <button
                                            key={s}
                                            type="button"
                                            onClick={() => update("star_target", s)}
                                            data-testid={`cost-star-${s}`}
                                            className={`flex-1 py-3 rounded-md border text-sm font-semibold transition ${
                                                form.star_target === s
                                                    ? "bg-gold text-dark-navy border-gold"
                                                    : "bg-white text-deep-blue border-slate-300 hover:border-gold/40"
                                            }`}
                                        >
                                            {Array.from({ length: s }).map(() => "★").join("")}
                                        </button>
                                    ))}
                                </div>
                            </Field>
                            <Field label="İl / Lokasyon" Icon={MapPin}>
                                <select
                                    value={form.city_code}
                                    onChange={(e) => update("city_code", e.target.value)}
                                    className="w-full px-3 py-2.5 rounded-md border border-slate-300 bg-white text-sm outline-none focus:border-teal"
                                    data-testid="cost-city"
                                >
                                    {(defaults?.cities || []).map((c) => (
                                        <option key={c.code} value={c.code}>
                                            {c.code} — {c.name}
                                        </option>
                                    ))}
                                </select>
                            </Field>
                            <Field label={`Oda Sayısı: ${form.room_count}`} Icon={Hash}>
                                <div className="flex items-center gap-3">
                                    <input
                                        type="range"
                                        min={20}
                                        max={500}
                                        step={5}
                                        value={form.room_count}
                                        onChange={(e) => update("room_count", parseInt(e.target.value, 10))}
                                        className="flex-1 accent-teal"
                                        data-testid="cost-rooms-slider"
                                    />
                                    <input
                                        type="number"
                                        min={1}
                                        value={form.room_count}
                                        onChange={(e) => update("room_count", parseInt(e.target.value, 10) || 1)}
                                        className="w-20 px-2 py-2 rounded-md border border-slate-300 text-sm font-data text-center"
                                        data-testid="cost-rooms-input"
                                    />
                                </div>
                            </Field>
                        </div>
                        <div className="mt-8 flex justify-end">
                            <button
                                onClick={() => setStep(1)}
                                className="bg-dark-navy hover:bg-deep-blue text-white text-sm font-semibold px-5 py-3 rounded-md inline-flex items-center gap-2"
                                data-testid="cost-step1-next"
                            >
                                Devam <ArrowRight className="w-4 h-4" />
                            </button>
                        </div>
                    </Card>
                )}

                {step === 1 && (
                    <Card>
                        <h2 className="text-lg font-bold text-dark-navy">Adım 2 — Detaylar</h2>
                        <p className="text-sm text-deep-blue/70 mt-1">Bina durumu, oda dağılımı, ek alanlar ve konsept.</p>
                        <div className="mt-6 space-y-6">
                            <Field label="Arazi / Bina Durumu" Icon={Hammer}>
                                <div className="grid grid-cols-2 gap-2">
                                    {(defaults?.building_status || []).map((b) => (
                                        <SelectChip
                                            key={b.value}
                                            active={form.building_status === b.value}
                                            onClick={() => update("building_status", b.value)}
                                            data-testid={`cost-building-${b.value}`}
                                        >
                                            {b.label}
                                        </SelectChip>
                                    ))}
                                </div>
                            </Field>

                            <Field label={`Oda Dağılımı (Toplam: ${totalDistributed} / ${form.room_count})`} Icon={Bed}>
                                <div className="grid grid-cols-3 gap-3">
                                    {[
                                        { key: "standard", label: "Standart", color: "teal" },
                                        { key: "deluxe", label: "Deluxe", color: "gold" },
                                        { key: "suite", label: "Suit", color: "deep-blue" },
                                    ].map((r) => (
                                        <div key={r.key} className="bg-light-bg rounded-md p-3">
                                            <div className="flex items-center justify-between text-xs uppercase tracking-wider text-deep-blue/80 font-semibold">
                                                <span>{r.label}</span>
                                                <span className="font-data text-dark-navy">{form.room_distribution[r.key]}</span>
                                            </div>
                                            <input
                                                type="range"
                                                min={0}
                                                max={form.room_count}
                                                value={form.room_distribution[r.key]}
                                                onChange={(e) => update(`room_distribution.${r.key}`, parseInt(e.target.value, 10))}
                                                className="w-full mt-2 accent-teal"
                                                data-testid={`cost-room-dist-${r.key}`}
                                            />
                                        </div>
                                    ))}
                                </div>
                                {totalDistributed !== form.room_count && (
                                    <p className="mt-2 text-xs text-data-red font-semibold">
                                        Uyarı: dağılım toplamı oda sayısına eşit değil ({totalDistributed} / {form.room_count}).
                                    </p>
                                )}
                            </Field>

                            <Field label="Ek Alanlar">
                                <div className="grid sm:grid-cols-2 gap-3">
                                    <ExtraToggle
                                        Icon={Utensils}
                                        label="Restoran"
                                        active={form.extras.restaurant.exists}
                                        onToggle={(v) => update("extras.restaurant.exists", v)}
                                        capacity={form.extras.restaurant.capacity}
                                        onCapacity={(v) => update("extras.restaurant.capacity", v)}
                                        capacityLabel="Kişi"
                                        testId="cost-restaurant"
                                    />
                                    <ExtraToggle
                                        Icon={Users}
                                        label="Toplantı Salonu"
                                        active={form.extras.meeting_room.exists}
                                        onToggle={(v) => update("extras.meeting_room.exists", v)}
                                        capacity={form.extras.meeting_room.capacity}
                                        onCapacity={(v) => update("extras.meeting_room.capacity", v)}
                                        capacityLabel="Kişi"
                                        testId="cost-meeting"
                                    />
                                    <SimpleToggle
                                        Icon={SpaIcon}
                                        label="Spa / Wellness"
                                        active={form.extras.spa}
                                        onToggle={(v) => update("extras.spa", v)}
                                        testId="cost-spa"
                                    />
                                    <SimpleToggle
                                        Icon={Waves}
                                        label="Yüzme Havuzu"
                                        active={form.extras.pool}
                                        onToggle={(v) => update("extras.pool", v)}
                                        testId="cost-pool"
                                    />
                                </div>
                            </Field>

                            <Field label="Konsept">
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                    {(defaults?.concepts || []).map((c) => (
                                        <SelectChip
                                            key={c.value}
                                            active={form.concept === c.value}
                                            onClick={() => update("concept", c.value)}
                                            data-testid={`cost-concept-${c.value}`}
                                        >
                                            {c.label}
                                        </SelectChip>
                                    ))}
                                </div>
                            </Field>
                        </div>
                        <div className="mt-8 flex justify-between">
                            <button
                                onClick={() => setStep(0)}
                                className="text-sm font-semibold text-deep-blue px-4 py-2.5 rounded-md hover:bg-light-bg inline-flex items-center gap-2"
                            >
                                <ArrowLeft className="w-4 h-4" /> Geri
                            </button>
                            <button
                                onClick={calculate}
                                disabled={loading}
                                className="bg-gold hover:bg-gold-700 text-dark-navy text-sm font-bold px-6 py-3 rounded-md inline-flex items-center gap-2 disabled:opacity-70 shadow-md"
                                data-testid="cost-calculate-btn"
                            >
                                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalcIcon className="w-4 h-4" />}
                                Hesapla
                            </button>
                        </div>
                    </Card>
                )}

                {step === 2 && result && (
                    <ResultStep
                        result={result}
                        cityName={cityName}
                        form={form}
                        onNext={() => setStep(3)}
                        onBack={() => setStep(1)}
                        onShare={() => setShareModal(true)}
                    />
                )}

                {step === 3 && result && (
                    <Card>
                        <h2 className="text-lg font-bold text-dark-navy">Adım 4 — Aksiyonlar</h2>
                        <p className="text-sm text-deep-blue/70 mt-1">Raporunuzu indirin, paylaşın veya uzman desteği alın.</p>
                        <div className="mt-6 grid sm:grid-cols-2 gap-4">
                            <ActionCard
                                Icon={Download}
                                title="PDF İndir"
                                desc="Detaylı maliyet raporunu PDF olarak indirin."
                                cta="PDF İndir"
                                onClick={downloadPdf}
                                accent="gold"
                                testId="cost-action-pdf"
                            />
                            <ActionCard
                                Icon={Share2}
                                title="Sosyal Medyada Paylaş"
                                desc="Sonucunuzu görsel kart ile X/LinkedIn/WhatsApp üzerinden paylaşın."
                                cta="Paylaş"
                                onClick={() => setShareModal(true)}
                                accent="teal"
                                testId="cost-action-share"
                            />
                            <ActionCard
                                Icon={Mail}
                                title="E-posta ile rapor"
                                desc="Raporu e-posta hesabınıza alın ve sektör bültenine kayıt olun."
                                cta="E-posta gönder"
                                onClick={() => setEmailModal(true)}
                                accent="deep-blue"
                                testId="cost-action-email"
                            />
                            <ActionCard
                                Icon={PhoneCall}
                                title="Uzman Danışmanlık"
                                desc="Sonuçları uzmanla görüşün, detaylı fizibiliteye dönüştürün."
                                cta="İletişime Geç"
                                href="/iletisim"
                                accent="deep-blue"
                                testId="cost-action-consult"
                            />
                            <ActionCard
                                Icon={RefreshCw}
                                title="Yeni Hesaplama"
                                desc="Farklı senaryolarla yeniden hesaplayın."
                                cta="Sıfırla"
                                onClick={reset}
                                accent="slate"
                                testId="cost-action-reset"
                            />
                        </div>
                    </Card>
                )}
            </div>

            {emailModal && (
                <EmailReportDialog form={form} onClose={() => setEmailModal(false)} />
            )}

            {shareModal && result && (
                <ShareDialog
                    open={shareModal}
                    onClose={() => setShareModal(false)}
                    tool="cost"
                    summary={{
                        total_cost: result.total_cost,
                        cost_per_room: result.cost_per_room,
                        estimated_roi_years: result.estimated_roi_years,
                        star: form.star_target,
                        room_count: form.room_count,
                        city_code: form.city_code,
                        facility_type: form.facility_type,
                        concept: form.concept,
                    }}
                />
            )}
        </div>
    );
}

function ResultStep({ result, cityName, form, onNext, onBack, onShare }) {
    const breakdownData = useMemo(
        () =>
            Object.entries(result.breakdown_amounts).map(([k, v]) => ({
                name: BREAKDOWN_LABELS[k] || k,
                value: v,
            })),
        [result],
    );

    const benchmark = result.benchmark_comparison;
    const benchmarkPositive = benchmark.diff_percentage > 0;

    return (
        <div className="space-y-6" data-testid="cost-result">
            <div className="bg-dark-navy rounded-xl p-7 text-white relative overflow-hidden">
                <div className="absolute inset-0 opacity-15" style={{ backgroundImage: "radial-gradient(circle at 30% 0%, rgba(214,176,107,0.5), transparent 60%)" }} />
                <div className="relative">
                    <span className="text-xs uppercase tracking-[0.2em] text-teal font-semibold">Toplam Tahmini Yatırım</span>
                    <div className="mt-3 font-data text-4xl sm:text-6xl font-bold text-gold tabular-nums">
                        <CountUp end={result.total_cost} duration={1.6} separator="." decimals={0} />
                        <span className="ml-2 text-2xl text-white/80">₺</span>
                    </div>
                    <div className="mt-4 grid sm:grid-cols-3 gap-3">
                        <SmallStat label="Oda Başı Maliyet" value={result.cost_per_room} suffix="₺" />
                        <SmallStat label="Geri Dönüş" value={result.estimated_roi_years} suffix=" yıl" decimals={1} />
                        <SmallStat label={`${cityName} · ${form.star_target}★`} value={form.room_count} suffix=" oda" />
                    </div>
                </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                <Card>
                    <h3 className="text-sm font-semibold uppercase tracking-wider text-deep-blue mb-3">Maliyet Dağılımı</h3>
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={breakdownData} dataKey="value" nameKey="name" innerRadius={60} outerRadius={95} paddingAngle={2}>
                                    {breakdownData.map((_, i) => (
                                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip
                                    contentStyle={{ borderRadius: 8, fontSize: 12 }}
                                    formatter={(v) => `${(v / 1_000_000).toFixed(1)}M ₺`}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-1.5 text-xs">
                        {breakdownData.map((d, i) => (
                            <div key={d.name} className="flex items-center gap-1.5">
                                <span className="w-2 h-2 rounded-sm" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                                <span className="text-deep-blue/80">{d.name}</span>
                                <span className="ml-auto font-data text-dark-navy font-semibold">
                                    {(d.value / 1_000_000).toFixed(1)}M
                                </span>
                            </div>
                        ))}
                    </div>
                </Card>

                <Card>
                    <h3 className="text-sm font-semibold uppercase tracking-wider text-deep-blue mb-3">Sektör Karşılaştırması</h3>
                    <div className="space-y-3">
                        <BenchmarkRow label="Ulusal ortalama oda başı" value={benchmark.national_average_per_room} />
                        <BenchmarkRow label="Sizin oda başı maliyet" value={benchmark.your_cost_per_room} highlight />
                        <div className="rounded-md bg-light-bg p-4">
                            <span className="text-xs uppercase tracking-wider text-deep-blue/80 font-semibold">Fark</span>
                            <div className={`mt-1 font-data text-2xl font-bold ${benchmarkPositive ? "text-data-red" : "text-data-green"}`}>
                                {benchmarkPositive ? "+" : ""}{benchmark.diff_percentage}%
                            </div>
                            <p className="mt-1 text-xs text-deep-blue/70">
                                {benchmarkPositive
                                    ? "Maliyet ulusal ortalamanın üzerinde — premium konsept veya zorlu lokasyon olabilir."
                                    : "Maliyet ulusal ortalamanın altında — ölçek avantajı veya verimli yapılanma."}
                            </p>
                        </div>
                    </div>
                    <div className="mt-4 p-3 rounded-md bg-gold/10 border-l-4 border-gold text-xs text-deep-blue/85">
                        <strong>Disclaimer:</strong> Bu rakamlar tahmini değerlerdir. Bölgesel pazar koşulları ve proje özelinde gereksinimlere göre %15-25 sapma gösterebilir.
                    </div>
                </Card>
            </div>

            <div className="flex items-center justify-between gap-2 flex-wrap">
                <button
                    onClick={onBack}
                    className="text-sm font-semibold text-deep-blue px-4 py-2.5 rounded-md hover:bg-light-bg inline-flex items-center gap-2"
                >
                    <ArrowLeft className="w-4 h-4" /> Düzenle
                </button>
                <div className="flex items-center gap-2">
                    <button
                        onClick={onShare}
                        className="bg-teal hover:bg-teal-700 text-dark-navy text-sm font-bold px-5 py-3 rounded-md inline-flex items-center gap-2"
                        data-testid="cost-result-share"
                    >
                        <Share2 className="w-4 h-4" /> Paylaş
                    </button>
                    <button
                        onClick={onNext}
                        className="bg-dark-navy hover:bg-deep-blue text-white text-sm font-semibold px-5 py-3 rounded-md inline-flex items-center gap-2"
                        data-testid="cost-result-next"
                    >
                        Aksiyonlar <ArrowRight className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    );
}

function EmailReportDialog({ form, onClose }) {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/newsletter/subscribe", {
                email: email.trim(),
                source: "tool_report",
                lang: "tr",
            });
            // Trigger the same PDF download — keep simple in this phase
            const res = await api.post("/calculator/cost/pdf", form, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = "otelpro-maliyet-raporu.pdf";
            a.click();
            window.URL.revokeObjectURL(url);
            toast.success("Bültene kayıt oldunuz, raporu indirdiniz.");
            onClose();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-md" data-testid="cost-email-dialog">
                <DialogHeader>
                    <DialogTitle>Raporu E-posta ile Alın</DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                    <p className="text-sm text-deep-blue/80">E-posta verirseniz raporu indireceğiz ve sektör bültenine kayıt olacaksınız.</p>
                    <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="ornek@firma.com"
                        className="w-full px-3 py-2.5 rounded-md border border-slate-300 text-sm outline-none focus:border-teal"
                        data-testid="cost-email-input"
                    />
                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-dark-navy hover:bg-deep-blue text-white text-sm font-semibold py-3 rounded-md inline-flex items-center justify-center gap-2 disabled:opacity-70"
                        data-testid="cost-email-submit"
                    >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                        Gönder & İndir
                    </button>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function Card({ children }) {
    return <div className="bg-white rounded-lg border border-slate-100 shadow-md p-7">{children}</div>;
}

function Field({ label, Icon, children }) {
    return (
        <div>
            <span className="text-xs font-semibold text-deep-blue/80 uppercase tracking-wider inline-flex items-center gap-1.5">
                {Icon && <Icon className="w-3.5 h-3.5 text-teal" />}
                {label}
            </span>
            <div className="mt-2">{children}</div>
        </div>
    );
}

function SelectChip({ active, onClick, children, ...rest }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`px-3 py-2.5 rounded-md border text-sm font-semibold transition ${
                active
                    ? "bg-dark-navy text-white border-dark-navy"
                    : "bg-white text-deep-blue border-slate-300 hover:border-teal/50"
            }`}
            {...rest}
        >
            {children}
        </button>
    );
}

function ExtraToggle({ Icon, label, active, onToggle, capacity, onCapacity, capacityLabel, testId }) {
    return (
        <div className={`rounded-md border p-3 transition ${active ? "border-teal bg-teal/5" : "border-slate-200 bg-white"}`}>
            <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-2 text-sm font-semibold text-dark-navy">
                    <Icon className="w-4 h-4 text-teal" />
                    {label}
                </span>
                <Switch checked={!!active} onCheckedChange={onToggle} data-testid={`${testId}-toggle`} />
            </div>
            {active && (
                <div className="mt-3 flex items-center gap-2">
                    <span className="text-xs text-deep-blue/70 uppercase tracking-wider font-semibold">{capacityLabel}</span>
                    <input
                        type="number"
                        min={0}
                        value={capacity}
                        onChange={(e) => onCapacity(parseInt(e.target.value, 10) || 0)}
                        className="w-20 px-2 py-1 rounded border border-slate-300 text-sm font-data text-center"
                        data-testid={`${testId}-capacity`}
                    />
                </div>
            )}
        </div>
    );
}

function SimpleToggle({ Icon, label, active, onToggle, testId }) {
    return (
        <div className={`rounded-md border p-3 transition flex items-center justify-between ${active ? "border-teal bg-teal/5" : "border-slate-200 bg-white"}`}>
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-dark-navy">
                <Icon className="w-4 h-4 text-teal" />
                {label}
            </span>
            <Switch checked={!!active} onCheckedChange={onToggle} data-testid={`${testId}-toggle`} />
        </div>
    );
}

function SmallStat({ label, value, suffix, decimals = 0 }) {
    return (
        <div className="rounded-md bg-white/5 border border-white/10 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-white/55 font-semibold">{label}</div>
            <div className="font-data text-lg font-bold text-white">
                <CountUp end={value} duration={1.4} decimals={decimals} separator="." />{suffix}
            </div>
        </div>
    );
}

function BenchmarkRow({ label, value, highlight }) {
    return (
        <div className="flex items-center justify-between border-b border-slate-100 pb-2">
            <span className="text-sm text-deep-blue/80">{label}</span>
            <span className={`font-data text-sm font-semibold ${highlight ? "text-dark-navy" : "text-deep-blue"}`}>
                {(value || 0).toLocaleString("tr-TR")} ₺
            </span>
        </div>
    );
}

function ActionCard({ Icon, title, desc, cta, onClick, href, accent, testId }) {
    const accentBg = accent === "gold" ? "bg-gold/15 text-gold-700" : accent === "teal" ? "bg-teal/10 text-teal-700" : accent === "deep-blue" ? "bg-deep-blue/10 text-deep-blue" : "bg-slate-100 text-slate-600";
    const ctaBg = accent === "gold" ? "bg-gold hover:bg-gold-700 text-dark-navy" : accent === "teal" ? "bg-teal hover:bg-teal-700 text-dark-navy" : accent === "deep-blue" ? "bg-deep-blue hover:bg-dark-navy text-white" : "bg-dark-navy hover:bg-deep-blue text-white";
    const inner = (
        <>
            <span className={`inline-flex w-12 h-12 rounded-md items-center justify-center ${accentBg}`}>
                <Icon className="w-5 h-5" />
            </span>
            <h4 className="mt-4 text-base font-bold text-dark-navy">{title}</h4>
            <p className="mt-1 text-sm text-deep-blue/80">{desc}</p>
        </>
    );
    return (
        <div className="lift-card bg-white rounded-lg border border-slate-100 shadow-md hover:shadow-xl p-5 flex flex-col">
            {inner}
            {href ? (
                <Link
                    to={href}
                    data-testid={testId}
                    className={`mt-5 inline-flex items-center justify-center gap-2 font-semibold text-sm px-4 py-2.5 rounded-md ${ctaBg}`}
                >
                    {cta}
                </Link>
            ) : (
                <button
                    onClick={onClick}
                    data-testid={testId}
                    className={`mt-5 inline-flex items-center justify-center gap-2 font-semibold text-sm px-4 py-2.5 rounded-md ${ctaBg}`}
                >
                    {cta}
                </button>
            )}
        </div>
    );
}
